import { closeButton } from '../ui/hud.js';
import { uiText } from '../ui/text.js';
import './editor.css';

/**
 * The world editor's window (ADR 0022): what is selected, how many edits
 * wait to be saved, what is wrong with them, the palette of what can be set
 * down, and the keys. The editor decides; the window only shows and asks.
 */
export interface EditorPanelOptions {
  /** False in production: the edits file is part of the build there. */
  canSave: boolean;
  /** The palette's element (`Palette.root`), placed under the buttons. */
  palette: HTMLElement;
  onClose: () => void;
  onSave: () => void;
  onUndo: () => void;
  /** The selected spawn's size, as typed. */
  onSpawnSize: (size: { count: number; radius: number }) => void;
}

export type MessageTone = 'info' | 'refused' | 'done';

const HELP = [
  'Glisser : deplacer',
  'Glisser le sol : bouger la vue (ou fleches)',
  'Molette ou - / + : zoom',
  'Suppr : retirer',
  'F : miroir',
  'R : tourner un habitant',
  'Ctrl+Z : annuler',
  'Ctrl+S : enregistrer',
  'Echap / F2 : quitter',
].join(' · ');

export class EditorPanel {
  private readonly root: HTMLElement;
  private readonly selected: HTMLElement;
  private readonly pending: HTMLElement;
  private readonly size: HTMLElement;
  private readonly count: HTMLInputElement;
  private readonly radius: HTMLInputElement;
  private readonly problems: HTMLUListElement;
  private readonly message: HTMLElement;
  private readonly save: HTMLButtonElement;

  constructor(private readonly options: EditorPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'editor';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Éditeur de carte'));

    const title = document.createElement('h2');
    title.className = 'editor__title';
    title.textContent = uiText('Éditeur de carte');
    const close = closeButton('Quitter l’éditeur (F2, Échap)', options.onClose);

    this.selected = line('editor__line editor__selected');
    this.pending = line('editor__line editor__muted');

    // How many a spawn keeps alive, and how far they wander.
    this.size = document.createElement('div');
    this.size.className = 'editor__row';
    this.size.hidden = true;
    this.count = numberInput('Nombre', 1, 20);
    this.radius = numberInput('Rayon', 0, 8);
    const changed = (): void => {
      options.onSpawnSize({ count: this.count.valueAsNumber, radius: this.radius.valueAsNumber });
    };
    this.count.addEventListener('change', changed);
    this.radius.addEventListener('change', changed);
    this.size.append(label('Nombre', this.count), label('Rayon', this.radius));

    const actions = document.createElement('div');
    actions.className = 'editor__row';
    const undo = button('Annuler');
    undo.addEventListener('click', options.onUndo);
    this.save = button('Enregistrer');
    this.save.disabled = true;
    this.save.addEventListener('click', options.onSave);
    if (!options.canSave) this.save.title = uiText('Enregistrement impossible en production');
    actions.append(undo, this.save);

    this.problems = document.createElement('ul');
    this.problems.className = 'editor__problems';
    this.message = line('editor__message');
    this.message.setAttribute('aria-live', 'polite');
    const help = line('editor__help');
    help.textContent = uiText(HELP);

    this.root.append(
      title,
      close,
      this.selected,
      this.size,
      this.pending,
      actions,
      this.problems,
      this.message,
      options.palette,
      help,
    );
    document.body.append(this.root);
  }

  set visible(visible: boolean) {
    this.root.hidden = !visible;
  }

  setSelected(text: string | undefined): void {
    this.selected.textContent = uiText(text ?? 'Rien de sélectionné : clique un élément');
  }

  /** Show the selected spawn's size, or hide the fields when no spawn is selected. */
  setSpawnSize(size: { count: number; radius: number } | undefined): void {
    this.size.hidden = size === undefined;
    if (size === undefined) return;
    // Leave a field being typed in alone.
    if (document.activeElement !== this.count) this.count.value = String(size.count);
    if (document.activeElement !== this.radius) this.radius.value = String(size.radius);
  }

  setState(count: number, dirty: boolean, problems: readonly string[]): void {
    const edits = `${String(count)} retouche${count > 1 ? 's' : ''}`;
    this.pending.textContent = uiText(
      dirty ? `${edits}, non enregistrées` : `${edits}, enregistrées`,
    );
    this.save.disabled = !this.options.canSave || !dirty || problems.length > 0;
    this.problems.replaceChildren(
      ...problems.slice(0, 8).map((problem) => {
        const item = document.createElement('li');
        item.textContent = uiText(problem);
        return item;
      }),
    );
    if (problems.length > 8) {
      const more = document.createElement('li');
      more.textContent = uiText(`… et ${String(problems.length - 8)} autres`);
      this.problems.append(more);
    }
  }

  say(text: string, tone: MessageTone = 'info'): void {
    this.message.textContent = uiText(text);
    this.message.dataset.tone = tone;
  }

  destroy(): void {
    this.root.remove();
  }
}

function line(className: string): HTMLElement {
  const element = document.createElement('p');
  element.className = className;
  return element;
}

function button(text: string): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = 'editor__button';
  element.textContent = uiText(text);
  return element;
}

function numberInput(name: string, min: number, max: number): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.className = 'editor__number';
  input.min = String(min);
  input.max = String(max);
  input.step = '1';
  input.setAttribute('aria-label', uiText(name));
  return input;
}

function label(text: string, input: HTMLInputElement): HTMLLabelElement {
  const element = document.createElement('label');
  element.className = 'editor__field';
  element.append(uiText(text), input);
  return element;
}
